import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { BookCheck, Upload, Loader2, Eye, CheckCircle2 } from "lucide-react";

const sel = "h-9 w-full rounded-lg border border-slate-200 bg-white px-2 text-sm";
const ST = { acknowledged: "bg-emerald-50 text-emerald-700", outstanding: "bg-slate-100 text-slate-700", overdue: "bg-rose-50 text-rose-700" };
export const StatusPill = ({ s, n }) => <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${n === 0 ? "bg-slate-50 text-slate-400" : ST[s] || ""}`}>{s}</span>;

function PublishDialog({ open, onClose, onDone, policy }) {
  const [f, setF] = useState({ title: "", audience_type: "all", audience_values: "", due_days: 14, reason: "" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!file) { toast.error("Choose the policy file"); return; }
    const fd = new FormData();
    if (policy) { fd.append("reason", f.reason); } else { ["title", "audience_type", "audience_values"].forEach((k) => fd.append(k, f[k])); }
    fd.append("due_days", f.due_days);
    fd.append("file", file);
    setBusy(true);
    try { await api.post(policy ? `/hr/policies/${policy.id}/versions` : "/hr/policies", fd); toast.success("Published"); onDone(); onClose(); }
    catch (e) { const dt = e.response?.data?.detail; toast.error(typeof dt === "string" ? dt : "Fill in every field (reason, deadline and file)"); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="policy-publish-dialog">
        <DialogHeader><DialogTitle>{policy ? `New version of ${policy.title}` : "Publish policy"}</DialogTitle></DialogHeader>
        <div className="grid gap-3">
          {policy ? <Input placeholder="What changed (required)" value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} data-testid="policy-reason" /> : (
            <>
              <Input placeholder="Policy title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="policy-title" />
              <select className={sel} value={f.audience_type} onChange={(e) => setF({ ...f, audience_type: e.target.value })} data-testid="policy-audience-type">
                <option value="all">All active staff</option><option value="roles">Specific roles</option><option value="users">Specific people</option>
              </select>
              {f.audience_type !== "all" && <Input placeholder={f.audience_type === "roles" ? "Role keys, comma separated (e.g. engineer, sales)" : "Employee IDs, comma separated"} value={f.audience_values} onChange={(e) => setF({ ...f, audience_values: e.target.value })} data-testid="policy-audience-values" />}
            </>
          )}
          <label className="text-xs text-slate-500">Acknowledge within (days)<Input type="number" min={1} max={365} value={f.due_days} onChange={(e) => setF({ ...f, due_days: e.target.value })} data-testid="policy-due-days" /></label>
          <input type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} className="text-sm" data-testid="policy-file" />
          <Button onClick={save} disabled={busy} data-testid="policy-publish-submit">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}Publish</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ReportDialog({ id, onClose }) {
  const [d, setD] = useState(null);
  useEffect(() => { setD(null); if (id) api.get(`/hr/policies/${id}/report`).then((r) => setD(r.data)).catch(() => onClose()); }, [id, onClose]);
  return (
    <Dialog open={!!id} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto pt-10" data-testid="policy-report-dialog">
        <DialogHeader><DialogTitle>{d ? `${d.policy.title} · v${d.policy.version}` : "Report"}</DialogTitle></DialogHeader>
        {!d ? <Loader2 className="h-5 w-5 animate-spin" /> : (
          <>
          <ul className="divide-y divide-slate-100 rounded-xl border border-slate-200">
            {d.rows.map((r) => <li key={r.user_id} className="flex items-center justify-between px-3 py-2 text-sm" data-testid={`policy-report-row-${r.user_id}`}><span>{r.name}</span><span className="flex items-center gap-2 text-xs text-slate-500">{r.acknowledged_at?.slice(0, 16).replace("T", " ")}<StatusPill s={r.status} /></span></li>)}
          </ul>
          <h3 className="mt-4 text-xs font-semibold uppercase tracking-wide text-slate-500">Full acknowledgement history</h3>
          <ul className="mt-1 divide-y divide-slate-100 rounded-xl border border-slate-200" data-testid="policy-history">
            {d.history.map((h) => <li key={h.id} className="px-3 py-1.5 text-xs text-slate-600">{h.user_name} · v{h.version} · {h.acknowledged_at.slice(0, 16).replace("T", " ")}{h.voided ? " · voided (correction kept)" : ""}</li>)}
          </ul>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function PoliciesTab({ manage }) {
  const [rows, setRows] = useState(null);
  const [pub, setPub] = useState(null);
  const [rep, setRep] = useState(null);
  const load = useCallback(() => { api.get("/hr/policies").then((r) => setRows(r.data.policies)).catch((e) => setRows(e.response?.status === 404 ? "off" : [])); }, []);
  useEffect(() => { load(); }, [load]);
  const closeRep = useCallback(() => setRep(null), []);
  if (rows === "off") return null;
  return (
    <div className="mt-6" data-testid="hr-policies-tab">
      <div className="flex items-center justify-between"><h2 className="flex items-center gap-2 text-base font-semibold text-slate-900"><BookCheck className="h-4 w-4 text-indigo-500" />Policies</h2>
        {manage && <Button onClick={() => setPub({})} data-testid="policy-new-btn"><Upload className="h-4 w-4" />Publish policy</Button>}</div>
      {rows === null ? <Loader2 className="mt-4 h-5 w-5 animate-spin" /> : rows.length === 0 ? <div className="mt-3 rounded-2xl border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500" data-testid="policies-empty">No policies published yet.</div> : (
        <div className="mt-3 divide-y divide-slate-100 rounded-2xl border border-slate-200 bg-white">
          {rows.map((p) => (
            <div key={p.id} className="flex flex-wrap items-center justify-between gap-2 px-4 py-3" data-testid={`policy-row-${p.id}`}>
              <div><div className="text-sm font-medium text-slate-800">{p.title} <span className="text-xs text-slate-500">v{p.version} · due {p.due_at}{p.status !== "active" ? " · retired" : ""}</span></div>
                <div className="mt-1 flex gap-2 text-xs"><StatusPill s="acknowledged" n={p.counts.acknowledged} />{p.counts.acknowledged}<StatusPill s="outstanding" n={p.counts.outstanding} />{p.counts.outstanding}<StatusPill s="overdue" n={p.counts.overdue} />{p.counts.overdue}</div></div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => setRep(p.id)} data-testid={`policy-report-${p.id}`}>Report</Button>
                {manage && p.status === "active" && <Button size="sm" variant="outline" onClick={() => setPub(p)} data-testid={`policy-version-${p.id}`}>New version</Button>}
              </div>
            </div>
          ))}
        </div>
      )}
      {pub && <PublishDialog open onClose={() => setPub(null)} onDone={load} policy={pub.id ? pub : null} />}
      <ReportDialog id={rep} onClose={closeRep} />
    </div>
  );
}

function AckRow({ p, statement, onDone }) {
  const [tick, setTick] = useState(false);
  const [busy, setBusy] = useState(false);
  const open = async () => {
    const w = window.open("about:blank", "_blank");
    try { const r = await api.get(`/hr/me/policies/${p.id}/file`); if (w) w.location.href = r.data.url; else window.location.href = r.data.url; onDone(); }
    catch (e) { if (w) w.close(); toast.error(e.response?.data?.detail || "Not available yet"); }
  };
  const ack = async () => {
    setBusy(true);
    try { await api.post(`/hr/me/policies/${p.id}/acknowledge`, { version: p.version, confirmed: true, statement }); toast.success("Acknowledged"); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  return (
    <li className="py-3" data-testid={`my-policy-${p.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm font-medium text-slate-800">{p.title} <span className="text-xs text-slate-500">v{p.version} · due {p.due_at}</span></div>
        <div className="flex items-center gap-2"><StatusPill s={p.status} />
          <button disabled={!p.downloadable} onClick={open} className="inline-flex h-8 items-center gap-1 rounded-lg border border-slate-200 px-2.5 text-xs disabled:opacity-40" data-testid={`my-policy-open-${p.id}`}><Eye className="h-3.5 w-3.5" />Open</button></div>
      </div>
      {p.status !== "acknowledged" && (
        <div className="mt-2 flex flex-wrap items-center gap-3 rounded-lg bg-slate-50 p-2">
          <label className={`flex items-center gap-2 text-sm ${p.opened ? "text-slate-700" : "text-slate-400"}`}><input type="checkbox" disabled={!p.opened} checked={tick} onChange={(e) => setTick(e.target.checked)} data-testid={`my-policy-tick-${p.id}`} />{statement}</label>
          <Button size="sm" disabled={!tick || !p.opened || busy} onClick={ack} data-testid={`my-policy-ack-${p.id}`}><CheckCircle2 className="h-4 w-4" />Confirm acknowledgement</Button>
          {!p.opened && <span className="text-xs text-amber-600">Open the policy first</span>}
        </div>
      )}
    </li>
  );
}

export function MyPolicies() {
  const [d, setD] = useState(null);
  const load = useCallback(() => { api.get("/hr/me/policies").then((r) => setD(r.data)).catch(() => setD({ policies: [], history: [] })); }, []);
  useEffect(() => { load(); }, [load]);
  if (!d || d.policies.length === 0) return null;
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="my-policies">
      <h2 className="flex items-center gap-2 text-base font-semibold text-slate-900"><BookCheck className="h-4 w-4 text-indigo-500" />Policies to read</h2>
      <ul className="mt-2 divide-y divide-slate-100">{d.policies.map((p) => <AckRow key={`${p.id}-${p.version}`} p={p} statement={d.statement} onDone={load} />)}</ul>
    </div>
  );
}
